import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { createContactMail } from "../actions/contactMailActions";
import {
  CONTACTMAIL_CREATE_FAIL,
  CONTACTMAIL_CREATE_RESET,
} from "../constants/contactMailConstants";

import { requiredFieldsErrorMessage } from "../constants/helpers";
import { useBreakpoints } from "../hooks/useBreakPoints";

import { ReactComponent as ScrollIndicator } from "../assets/img/home/bg-scroll-indicator.svg";
import { ReactComponent as BgSms } from "../assets/img/home/bg-sms.svg";
import { ReactComponent as BgSmsMd } from "../assets/img/home/bg-sms-md.svg";
import Logo from "../assets/logos/logo-peh-yellow.png";
import AppSlide from "../components/AppSlide";
import { AppTimeline } from "../components/AppTimeline";
import AppAlert from "../components/AppAlert";
import Loader from "../components/Loader";
import EventDetail from "../components/EventDetail";
import AppCarousel from "../components/AppCarousel";

const HomeScreen = () => {
  const { md, lg } = useBreakpoints();
  const dispatch = useDispatch();
  const [showGoTopButton, setShowGoTopButton] = useState(false);

  const contactMailCreate = useSelector((state) => state.contactMailCreate);
  const { loading, error: failed, success: sent } = contactMailCreate;

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  const nameHandler = (e) => {
    setName(e.target.value);
  };

  const emailHandler = (e) => {
    setEmail(e.target.value);
  };

  const subjectHandler = (e) => {
    setSubject(e.target.value);
  };

  const messageHandler = (e) => {
    setMessage(e.target.value);
  };

  const submitHandler = (e) => {
    e.preventDefault();

    if (
      name.length > 0 &&
      email.length > 0 &&
      subject.length > 0 &&
      message.length > 0
    ) {
      const contactMail = {
        name,
        email,
        subject,
        message,
      };

      dispatch(createContactMail(contactMail));
    } else {
      dispatch({
        type: CONTACTMAIL_CREATE_FAIL,
        payload: requiredFieldsErrorMessage,
      });
    }
  };

  useEffect(() => {
    if (failed || sent) {
      if (sent) {
        setName("");
        setEmail("");
        setSubject("");
        setMessage("");
      }

      setTimeout(() => {
        dispatch({ type: CONTACTMAIL_CREATE_RESET });
      }, 7000);
    }
  }, [failed, sent, dispatch]);

  const scrollToContact = () => {
    window.scrollTo({ top: 15800, behavior: "smooth" });
    console.log(window.scrollTo);
  };

  const scrollTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    document.addEventListener("scroll", () => {
      setShowGoTopButton(document.documentElement.scrollTop > 460);
    });
  }, []);

  const events = [
    {
      cardImg: require("../assets/img/home/fashion-show-modal-mobil.jpg"),
      date: "October 14, 12 pm - 4 pm",
      imgModal: require(lg
        ? "../assets/img/home/fashion-show-modal.jpg"
        : "../assets/img/home/fashion-show-modal-mobil.jpg"),
      title: "Victorian Fashion Show",
      price: " $30/adult - $20/youth (<17)",
      descripcion:
        "Step back in time to the opulent Victorian era at Point Ellice House Museum's splendid fashion show, where authentic 19th-century attire graces the catwalk. Indulge in culinary delights while admiring the era's fashion, and explore the preserved historic house with period decor. Don't miss this fusion of fashion, history, and culture - secure your spot today for a truly enchanting experience. Let's make history stylish together!",
      link: "https://square.link/u/zuL53NTH",
    },
    {
      cardImg: require(lg
        ? "../assets/img/home/spider-brooch.jpg"
        : "../assets/img/home/spider-brooch-mobile.jpg"),
      date: "October 18 & 25, 2 pm - 3 pm",
      imgModal: require(!lg
        ? "../assets/img/home/spider-brooch-mobile.jpg"
        : "../assets/img/home/spider-brooch.jpg"),
      styleImgModal: "bg-top",
      title: "Beaded Spider Brooch Workshop",
      price: "$25/person",
      descripcion:
        "Step into the world of Victorian macabre at Point Ellice House Museum's Beaded Spider Brooch Workshop! Craft your own spider brooch within our 19th-century historic house while surrounded by period-appropriate decor. Join us for a uniquely historic experience and let's spin creative webs together!",
      link: "https://square.link/u/nPH7636I",
    },
    {
      cardImg: require("../assets/img/home/winter-wreath-mobile.jpg"),
      date: "November 15 & 22, 2 pm - 3 pm",
      imgModal: require(lg
        ? "../assets/img/home/winter-wreath-mobile.jpg"
        : "../assets/img/home/winter-wreath.jpg"),
      title: "Winter Wreath Workshop",
      price: "$40/person",
      descripcion:
        "Embrace the season's enchanting beauty at Point Ellice House Museum's Winter Wreath Workshop! Craft exquisite wreaths using garden-fresh botanicals, guided by our expert Heritage Garden Manager. Immerse yourself in history amidst the charming backdrop of Point Ellice House and bring the magic of winter home. Join us for a cozy day of creativity, history, and seasonal charm!",
      link: "https://square.link/u/VDu17t8d",
    },
  ];

  return (
    <div className="bg-primary relative">
      {loading && <Loader />}
      {sent && (
        <AppAlert
          type="success"
          message="Email sent successfully! Thank you for your interest!"
        />
      )}
      {failed && (
        <AppAlert
          type="error"
          message="Failed to send the contact mail! Please try again!"
        />
      )}
      <div className="container">
        <div className="flex pt-6 justify-center md:justify-between">
          <img src={Logo} alt="Logo" className="w-2/3 md:w-56" />
          <div className="flex gap-3">
            <Link
              to="/volunteer-application"
              className="hidden md:block h-fit py-3 px-9 text-primary bg-salmon uppercase with-transition hover:bg-white font-bold"
            >
              Volunteer
            </Link>
            <button
              onClick={scrollToContact}
              className="hidden md:block h-fit py-3 px-9 text-primary bg-secondary uppercase with-transition hover:bg-white font-bold"
            >
              Contact
            </button>
          </div>
        </div>
        <p className="text-xl mt-6 text-secondary text-center">
          WHERE HERITAGE BLOOMS
        </p>
        <p
          data-aos="zoom-in"
          data-aos-duration="1000"
          className="my-4 h-0.5 w-full bg-secondary md:w-2/5 mx-auto"
        />
        <h1 className="leading-none text-center mb-6 font-title offwhite-text">
          POINT ELLICE HOUSE
        </h1>
      </div>
      <AppSlide className="h-[378px] md:h-[470px] w-full">
        <img
          src={require("../assets/img/home/slider-1.jpg")}
          alt=""
          className="h-full w-full object-cover"
        />
        <img
          src={require(md
            ? "../assets/img/home/slider-2.jpg"
            : "../assets/img/home/slider-2-mobile.png")}
          alt=""
          className="h-full w-full object-cover"
        />
        <img
          src={require(md
            ? "../assets/img/home/slider-3.jpg"
            : "../assets/img/home/slider-3-mobile.png")}
          alt=""
          className="h-full w-full object-cover"
        />
      </AppSlide>
      <div className="relative">
        <div className="font-normal hidden md:flex pt-4 pb-6 px-5 w-fit left-1/2 -translate-x-1/2 absolute -mt-[50px] items-center">
          <ScrollIndicator className="absolute left-1/2 -translate-x-1/2" />
          <div className="z-10 flex justify-center gap-3 items-center">
            <i
              className="iconify text-2xl text-secondary"
              data-icon="mingcute:mouse-fill"
            />
            <p className="font-normal text-xs offwhite-text">
              Scroll to see more
            </p>
          </div>
        </div>
      </div>
      <button
        onClick={scrollTop}
        className={`with-transition duration-300 text-primary bg-white hover:bg-secondary px-3 h-[43px] shadow-lg fixed bottom-4 right-4 md:bottom-10 md:right-10 text-4xl z-20
         ${showGoTopButton ? "opacity-100" : "opacity-0 pointer-events-none"} `}
      >
        <i className="iconify text-lg" data-icon="ep:arrow-up-bold" />
      </button>
      <div className="bg-white-peh py-12">
        <p className="container text-primary text-center md:w-2/3 md:mx-auto">
          The Point Ellice House Museum and Gardens is proud to be located
          within the traditional, ancestral, and unceded territory of the
          Lekwungen People, also known as the Songhees and Esquimalt First
          Nations.
        </p>
      </div>
      <div className="bg-salmon py-12 md:py-28">
        <div className="container">
          <img
            src={require("../assets/logos/logo-peh-green.png")}
            alt="Logo Point Ellice House "
            className="mx-auto w-20 mb-8"
          />
          <p className="text-primary md:text-center md:w-2/3 md:mx-auto">
            Nestled in the heart of Victoria, British Columbia, Point Ellice
            House beckons you to embark on a captivating journey through time.
            As a cherished national and provincial historic site, this
            architectural and horticultural gem has been lovingly maintained as
            a private residence and later museum since it was built in 1861.
            Standing tall as one of Victoria's oldest residences, Point Ellice
            House was initially built for Catherine and Charles Wallace. Its
            resplendent Italianate Villa-style design transports you to the
            elegant tapestry of the 19th century. With an enduring allure that
            has weathered the years, this remarkable abode invites people of all
            ages to bask in its unparalleled charm and discover the stories
            etched within its walls. Encounter the most extensive collection of
            Victorian-era artifacts in Canada, providing a vivid window into the
            opulence and intricacies of the past. Delight in the vibrant beauty
            of our gardens, meticulously cultivated and offering a serene oasis
            within the bustling city.
          </p>
        </div>
      </div>

      <div className="py-12 md:py-28 bg-white-peh">
        <div className="text-primary grid md:grid-cols-2 md:gap-12 md:container items-center">
          <div data-aos={md ? "fade-left" : ""} data-aos-duration="1000">
            <div className="container md:px-0">
              <h2 className="text-4xl uppercase">the house</h2>
              <p
                data-aos={md ? "fade-left" : "zoom-in"}
                data-aos-duration="2000"
                className="my-4 h-0.5 w-full md:w-1/2 bg-secondary"
              />
              <p className="mb-8 md:mb-0">
                Experience the unparalleled beauty of Point Ellice's prime
                location, offering breathtaking views of the Selkirk Water/Gorge
                Waterway. This historic site showcases the charm of a Victorian
                Italianate villa with its shallow-pitched gabled roofs, wide
                overhanging bracketed eaves, inviting verandas and porches, and
                tall spacious windows. When the House is open, you can immerse
                yourself in one of Canada's most extensive collections of
                Victorian-era artifacts and wander through rooms where decisions
                were made that have lasting impacts on Canadian society even
                today. From captivating paintings to exquisite furniture and
                intriguing books to vintage games, every visit is an opportunity
                to delve into a wealth of history waiting to be discovered.
              </p>
            </div>
          </div>
          <div data-aos="flip-left" data-aos-duration="2000">
            <div
              className="h-[206px] md:h-[324px] bg-cover bg-no-repeat bg-center"
              style={{
                backgroundImage: `url(${require("../assets/img/home/the_hause.jpg")})`,
              }}
            />
          </div>
        </div>

        <div className="text-primary mt-12 md:mt-28 grid md:grid-cols-2 md:gap-12 md:container items-center">
          <div
            data-aos={md ? "fade-right" : ""}
            data-aos-duration="1000"
            className="md:order-2"
          >
            <div className="container md:px-0">
              <h2 className="text-4xl uppercase">The Gardens</h2>
              <p
                data-aos={md ? "fade-letf" : "zoom-in"}
                data-aos-duration="2000"
                className="my-4 h-0.5 w-full md:w-1/2 bg-secondary"
              />
              <p className="mb-8 md:mb-0">
                Step into the captivating realm of Point Ellice House's historic
                gardens, where the essence of time unfolds in a symphony of
                natural wonders. Explore nearly two acres of meticulously
                curated landscapes adorned with a vibrant tapestry of trees,
                flowers, and shrubs, each whispering tales of the past. Marvel
                at the majestic Sequoia tree, a towering sentinel that stands as
                one of the most beloved elements of the heritage gardens. Embark
                on a self-guided garden tour and enjoy the space during one of
                the many events, where breathtaking views and fascinating
                history intertwine, inviting you to immerse yourself in the
                spellbinding beauty of Point Ellice House's enchanting gardens.
              </p>
            </div>
          </div>
          <div data-aos="flip-right" data-aos-duration="2000">
            <div
              className="h-[206px] md:h-[324px] bg-cover bg-no-repeat bg-center"
              style={{
                backgroundImage: `url(${require("../assets/img/home/the_gardens.jpg")})`,
              }}
            />
          </div>
        </div>
      </div>

      <div className="bg-primary grid md:grid-cols-2 gap-12 md:items-center">
        <div className="hidden md:block" />

        <div className="md:absolute w-full">
          <div className="container grid grid-cols-2">
            <div className="col-span-2 md:col-span-1 text-center md:text-start">
              <h2 className="uppercase text-4xl mt-12 offwhite-text">
                Temporary Exhibits
              </h2>
              <p
                data-aos="zoom-in"
                data-aos-duration="1000"
                className="mt-4 mb-4 h-0.5 w-full md:w-3/4 bg-secondary"
              />
              <p className="md:mb-8 offwhite-text md:pr-10">
                Throughout the year, temporary exhibits fill the Point Ellice
                House Visitor Centre, bringing a fresh subject to light with
                each new installation. Stories exploring the natural heritage of
                the region, animals that have called Point Ellice House home,
                and showcasing artistic achievements of current and historic
                artists that have felt the inspiration of Point Ellice House
                while visiting. Whether they incorporate specific objects from
                the history of Point Ellice House or welcome stories from
                farther afield, the temporary exhibits give visitors more
                reasons to make Point Ellice House a repeat destination.
              </p>
            </div>
          </div>
        </div>
        <img
          src={require("../assets/img/home/victoria.jpg")}
          alt=""
          className="h-[353px] md:h-[653px] w-full bg-cover bg-no-repeat bg-center"
        />
      </div>

      <div className="bg-white-peh text-primary py-12 md:py-28">
        <div className="md:text-center md:mx-auto">
          <h2 className="uppercase text-4xl pl-4 md:pl-0">Upcoming Events</h2>
          <p
            data-aos="zoom-in"
            data-aos-duration="2000"
            className="my-4 ml-4 h-0.5 w-80 md:w-1/3 md:mx-auto bg-secondary"
          />
          <div>
            <AppCarousel withArrows={md} center>
              <div className="lg:w-24" />
              {events.map((event, i) => (
                <EventDetail key={i} event={event} />
              ))}
              <div className="lg:w-24" />
            </AppCarousel>
          </div>
        </div>
      </div>
      <div className="bg-hours-of-operation text-center py-12 md:py-28">
        <div className="container">
          <h2 className="uppercase text-4xl offwhite-text">
            Hours of Operation
          </h2>
          <div
            data-aos="zoom-in"
            data-aos-duration="1000"
            className="my-4 xl:mb-12 h-0.5 w-full md:w-1/3 md:mx-auto bg-secondary"
          />
          <div className="w-full">
            <AppTimeline
              className="gap-8 xl:gap-2 justify-center pb-12"
              heigthLine="h-[68px]"
              items={[
                [<p>Monday</p>, <p>CLOSED</p>],
                [<p>TUESDAY</p>, <p>GROUPS (20+)</p>],
                [<p>WEDNESDAY</p>, <p>GROUPS (20+)</p>],
                [
                  <p className="h-10 my-auto xl:h-full pt-3 xl:pt-0">
                    THURSDAY
                  </p>,
                  <p className="h-10 my-auto xl:h-full pt-3 xl:pt-0">
                    10 - 4{" "}
                  </p>,
                ],
                [
                  <p className="h-10 my-auto xl:h-full pt-3 xl:pt-0">FRIDAY</p>,
                  <p className="h-10 my-auto xl:h-full pt-3 xl:pt-0">
                    10 - 4{" "}
                  </p>,
                ],
                [
                  <p className="h-10 my-auto xl:h-full pt-3 xl:pt-0">
                    SATURDAY
                  </p>,
                  <p className="h-10 my-auto xl:h-full pt-3 xl:pt-0">
                    10 - 4{" "}
                  </p>,
                ],
                [
                  <p
                    className="h-10 my-auto xl:h-full pt-3 xl:pt-0"
                    id="last-item"
                  >
                    Sunday
                  </p>,
                  <p className="h-10 my-auto xl:h-full pt-3 xl:pt-0">10 - 4</p>,
                ],
              ]}
            />
            <div className="relative grid place-items-center py-12 md:pt-0 md:pb-10">
              <BgSms className="absolute lg:hidden" />
              <BgSmsMd className="absolute hidden lg:block" />
              <p className="absolute text-primary font-bold text-center w-72 lg:w-full">
                The Point Ellice House Museum collection is open to the public
                between 12 pm and 2 pm Thursday to Sunday
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-salmon grid md:grid-cols-2 gap-12 md:items-center">
        <div
          className="h-[353px] md:h-[503px] w-full bg-cover bg-no-repeat bg-center"
          style={{
            backgroundImage: `url(${require("../assets/img/home/the_wonders.jpg")}`,
          }}
        />

        <div className="md:absolute w-full md:pl-12">
          <div className="container grid grid-cols-2">
            <div className="hidden md:block" />
            <div className="col-span-2 md:col-span-1 text-start">
              <h2 className="uppercase text-4xl md:mt-12 text-primary">
                Explore the Wonders of
                <span className="md:block"> Point Ellice House</span>
              </h2>
              <p
                data-aos="zoom-in"
                data-aos-duration="1000"
                className="mt-4 mb-4 h-0.5 w-full md:w-3/4 bg-secondary"
              />
              <p className="mb-12 md:mb-8 text-primary md:pr-10">
                Wander through our captivating Gardens and Visitor Center
                available during regular hours. For a more exclusive experience,
                the Museum House unveils its historical treasures from 12 pm to
                2 pm. Dive into a rich tapestry of history and stories that echo
                through time. Remember, every entrance fee is an investment in
                preserving the timeless beauty of Point Ellice House for future
                admirers.
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white-peh">
        <div className="container py-12 md:py-28 text-primary text-center">
          <h2 className="uppercase text-4xl">Entrance Fees</h2>
          <p
            data-aos="zoom-in"
            data-aos-duration="1000"
            className="my-4 lg:mb-12 h-0.5 w-full xl:w-1/3 md:mx-auto bg-secondary"
          />
          <div className="flex justify-between md:gap-4 xl:max-w-2xl xl:mx-auto">
            <p
              data-aos="zoom-in"
              data-aos-duration="1000"
              className="my-4 h-0.5 w-8 lg:w-[22%] md:ml-auto bg-secondary"
            />
            <p className="font-bold mb-8 text-xl uppercase">
              Daily Rates - Garden Only
            </p>
            <p
              data-aos="zoom-in"
              data-aos-duration="1000"
              className="my-4 h-0.5 w-8 lg:w-[22%] md:mr-auto bg-secondary"
            />
          </div>
          <AppTimeline
            className="gap-6 xl:gap-2 xl:max-w-4xl"
            colorLine="bg-primary"
            heigthLine="h-20"
            widthLine="w-[180px]"
            widthFirstItem="w-7"
            widthSecondItem="w-24"
            items={[
              [
                <div className="ml-auto lg:mx-auto w-fit text-primary">
                  <i className="iconify text-2xl" data-icon="uil:users-alt" />
                </div>,
                <p className="font-bold text-2xl">
                  $15
                  <span className="block font-normal normal-case text-base">
                    Family (x5)
                  </span>
                </p>,
              ],
              [
                <div className="ml-auto lg:mx-auto w-fit text-primary">
                  <i className="iconify text-2xl" data-icon="uil:user-md" />
                </div>,
                <p className="font-bold text-2xl">
                  $4
                  <span className="block font-normal normal-case text-base">
                    Seniors (60+)
                  </span>
                </p>,
              ],
              [
                <div className="ml-auto lg:mx-auto w-fit text-primary">
                  <i className="iconify text-2xl" data-icon="uil:user-nurse" />
                </div>,
                <p className="font-bold text-2xl">
                  $5
                  <span className="block font-normal normal-case text-base">
                    Adults (18-59)
                  </span>
                </p>,
              ],
              [
                <div className="ml-auto lg:mx-auto w-fit text-primary">
                  <i className="iconify text-2xl" data-icon="uil:user" />
                </div>,
                <p className="font-bold text-2xl">
                  $4
                  <span className="block font-normal normal-case text-base">
                    Youth (12-17)
                  </span>
                </p>,
              ],
              [
                <div
                  id="last-item"
                  className="ml-auto lg:mx-auto w-fit text-primary"
                >
                  <i className="iconify text-2xl" data-icon="uil:user" />
                </div>,
                <p className="font-bold text-2xl">
                  FREE
                  <span className="block font-normal normal-case text-base">
                    Children {"(<12) "}
                  </span>
                </p>,
              ],
            ]}
          />
          <div className="flex justify-between md:gap-4 xl:max-w-2xl xl:mx-auto my-12 lg:mt-20 lg:mb-8">
            <p
              data-aos="zoom-in"
              data-aos-duration="1000"
              className="my-4 h-0.5 w-14 lg:w-[22%] md:ml-auto bg-secondary"
            />
            <p className="font-bold text-xl uppercase">
              Daily Rates - Full Site
            </p>
            <p
              data-aos="zoom-in"
              data-aos-duration="1000"
              className="my-4 h-0.5 w-14 lg:w-[22%] md:mr-auto bg-secondary"
            />
          </div>
          <AppTimeline
            className="gap-6 xl:gap-2 xl:max-w-4xl"
            colorLine="bg-primary"
            heigthLine="h-20"
            widthLine="w-[180px]"
            widthFirstItem="w-7"
            widthSecondItem="w-24"
            items={[
              [
                <div className="ml-auto w- lg:mx-auto w-fit text-primary">
                  <i className="iconify text-2xl" data-icon="uil:users-alt" />
                </div>,
                <p className="font-bold text-2xl">
                  $35
                  <span className="block font-normal normal-case text-base">
                    Family (x5)
                  </span>
                </p>,
              ],
              [
                <div className="ml-auto lg:mx-auto w-fit text-primary">
                  <i className="iconify text-2xl" data-icon="uil:user-md" />
                </div>,
                <p className="font-bold text-2xl">
                  $10
                  <span className="block font-normal normal-case text-base">
                    Seniors (60+)
                  </span>
                </p>,
              ],
              [
                <div className="ml-auto lg:mx-auto w-fit text-primary">
                  <i className="iconify text-2xl" data-icon="uil:user-nurse" />
                </div>,
                <p className="font-bold text-2xl">
                  $12
                  <span className="block font-normal normal-case text-base">
                    Adults (18-59)
                  </span>
                </p>,
              ],
              [
                <div className="ml-auto lg:mx-auto w-fit text-primary">
                  <i className="iconify text-2xl" data-icon="uil:user" />
                </div>,
                <p className="font-bold text-2xl">
                  $10
                  <span className="block font-normal normal-case text-base">
                    Youth (12-17)
                  </span>
                </p>,
              ],
              [
                <div
                  id="last-item"
                  className="ml-auto lg:mx-auto w-fit text-primary"
                >
                  <i className="iconify text-2xl" data-icon="uil:user" />
                </div>,
                <p className="font-bold text-2xl">
                  FREE
                  <span className="block font-normal normal-case text-base">
                    Children {"(<12)"}
                  </span>
                </p>,
              ],
            ]}
          />
          <div className="mt-12 lg:mt-20 relative lg:w-fit lg:mx-auto">
            <span className="block h-[15px] w-[15px] rounded-full bg-white-peh absolute -top-2 -left-2" />
            <span className="block h-[15px] w-[15px] rounded-full bg-white-peh absolute -top-2 -right-2" />
            <span className="block h-[15px] w-[15px] rounded-full bg-white-peh absolute -bottom-2 -left-2" />
            <span className="block h-[15px] w-[15px] rounded-full bg-white-peh absolute -bottom-2 -right-2" />
            <p className="bg-secondary font-bold py-3 px-4">
              Groups of 10 or more receive a 20% off on entrance fees.
            </p>
          </div>
        </div>
      </div>
      <div className="bg-salmon">
        <div className="text-primary grid md:grid-cols-2 md:gap-12 container pt-12 md:items-center">
          <div className="mb-8">
            <h2 className="text-4xl uppercase">GET INVOLVED</h2>
            <p
              data-aos="zoom-in"
              data-aos-duration="1000"
              className="my-4 h-0.5 w-full md:w-2/3 bg-secondary"
            />
            <p className="mb-6">
              Join us in preserving history! In addition to visiting Point
              Ellice House, you can get involved and make a difference. Whether
              you are passionate about heritage preservation, volunteer work, or
              contributing to our programs, we welcome your support. Contact us
              to explore the various opportunities available, and we'll be
              delighted to assist you in becoming a valued community member.
            </p>
            <Link
              to="/volunteer-application"
              className="grid place-items-center h-fit py-3 px-9 w-full lg:w-fit text-white hover:text-primary hover:font-bold bg-primary hover:bg-white uppercase with-transition"
            >
              Get involved
            </Link>
          </div>
          <img
            src={require("../assets/img/home/flower.png")}
            alt=""
            className="w-1/2 md:w-2/6 mx-auto"
          />
        </div>
      </div>
      <div>
        <div id="contact" className="container">
          <div className="grid md:grid-cols-2">
            <div className="relative">
              <h2 className="text-4xl uppercase mt-12 offwhite-text">
                contact
              </h2>
              <p
                data-aos="zoom-in"
                className="my-4 h-0.5 w-full bg-secondary"
              />
              <p className="font-bold tracking-wide offwhite-text">
                STAY CONNECTED WITH US!
              </p>
              <p className="my-8 md:pr-10 offwhite-text">
                Point Ellice House Museum and Gardens is committed to fostering
                meaningful relationships within our community. We value your
                interest and support in preserving our heritage site. If you
                have inquiries, want to explore volunteer opportunities, or wish
                to collaborate with us to celebrate our shared cultural legacy,
                we invite you to contact us. Our dedicated team is eager to
                answer your questions and guide you toward engaging experiences.
                Reach out to us using the contact details below, and let's
                embark on a journey of discovery together.
              </p>
              <p className="mt-3 offwhite-text">
                Address:{" "}
                <span className="tracking-normal mt-1 font-normal block">
                  2616 Pleasant St, Victoria, BC V8T 4V3
                </span>
              </p>
              <p className="mt-3 offwhite-text">
                Phone:{" "}
                <span className="tracking-normal mt-1 font-normal block">
                  +1 (250) 800 1831
                </span>
              </p>
              <p className="mt-3 offwhite-text">
                General Enquiries:{" "}
                <span className="tracking-normal mt-1 font-normal block">
                  info@pointellicehouse.com
                </span>
              </p>
              <div className="flex justify-center lg:justify-start gap-6 lg:gap-4 mt-12 social-links">
                <Link
                  target="_blank"
                  to="https://www.facebook.com/PointEllicePHS"
                >
                  <i
                    className="iconify text-3xl"
                    data-icon="basil:facebook-solid"
                  />
                </Link>
                <Link target="_blank" to="https://twitter.com/pointellicephs">
                  <i className="iconify text-3xl" data-icon="mdi:twitter" />
                </Link>
                <Link target="_blank" to="https://instagram.com/pointellicephs">
                  <i className="iconify text-3xl" data-icon="mdi:instagram" />
                </Link>
                <Link
                  target="_blank"
                  to="updatehttps://www.linkedin.com/showcase/pointellicephs/"
                >
                  <i className="iconify text-3xl" data-icon="mdi:linkedin" />
                </Link>
                <Link
                  target="_blank"
                  to="https://www.tiktok.com/@pointellicephs"
                >
                  <i
                    className="iconify text-2xl"
                    data-icon="simple-icons:tiktok"
                  />
                </Link>
              </div>
            </div>
            <div>
              <h2 className="md:pl-10 text-4xl uppercase mt-12 offwhite-text">
                Get in touch
              </h2>
              <p
                data-aos="zoom-in"
                className="my-4 h-0.5 w-full bg-secondary"
              />
              <p className="md:pl-10 offwhite-text">
                Fields marked with ( <span className="text-secondary">*</span> )
                are required
              </p>
              <form
                className="md:pl-10 mt-8 grid grid-cols-2 gap-5"
                autoComplete="off"
                onSubmit={submitHandler}
              >
                <label className="col-span-2 md:col-span-1 offwhite-text">
                  Name <span className="text-secondary">*</span>
                  <input
                    type="text"
                    name="name"
                    className="h-10 bg-transparent border-[1px] offwhite-border w-full mt-2 p-2"
                    required
                    value={name}
                    onChange={nameHandler}
                  />
                </label>
                <label className="col-span-2 md:col-span-1 offwhite-text">
                  Email <span className="text-secondary">*</span>
                  <input
                    type="email"
                    name="email"
                    className="h-10 bg-transparent border-[1px] offwhite-border w-full mt-2 p-2"
                    required
                    value={email}
                    onChange={emailHandler}
                  />
                </label>
                <label className="col-span-2 offwhite-text">
                  Subject <span className="text-secondary">*</span>
                  <input
                    type="text"
                    name="subject"
                    className="h-10 bg-transparent border-[1px] offwhite-border w-full mt-2 p-2"
                    required
                    value={subject}
                    onChange={subjectHandler}
                  />
                </label>
                <label className="col-span-2 offwhite-text">
                  Message <span className="text-secondary">*</span>
                  <textarea
                    name="message"
                    className="h-32 md:h-40 bg-transparent border-[1px] offwhite-border w-full mt-2 p-2"
                    required
                    value={message}
                    onChange={messageHandler}
                  />
                </label>
                <input
                  type="submit"
                  value="Submit"
                  className="col-span-2 lg:mt-2 with-transition bg-secondary hover:bg-white uppercase font-bold w-full md:w-48 py-3 text-primary mx-auto md:ml-0 cursor-pointer"
                  onSubmit={submitHandler}
                />
              </form>
              <img
                src={require("../assets/logos/logo-peh-white.png")}
                alt=""
                className="w-1/2 mx-auto mt-12 md:hidden"
              />
            </div>
          </div>
          <div className="col-span-full mt-12 md:mt-28 pb-5">
            <span className="block w-full border-t-[1px] offwhite-border mb-2 mx-auto" />
            <p className="lg:flex lg:justify-between px-3 lg:px-0 text-xs text-center offwhite-text">
              Copyright © 2023, Forager Foundation
              <span className="block">
                website created by
                <span className="font-semibold tracking-wide">
                  {" "}
                  forager | <span className="text-orange-digital">digital</span>
                </span>
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeScreen;
